/*
 * Copyright 2021 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * External dependencies
 */
import styled from 'styled-components';
import { __ } from '@web-stories-wp/i18n';
import { useCallback, useMemo, useState } from '@web-stories-wp/react';
import PropTypes from 'prop-types';
import { v4 as uuidv4 } from 'uuid';
import {
  BUTTON_TRANSITION_TIMING,
  THEME_CONSTANTS,
  Text,
  useSnackbar,
} from '@web-stories-wp/design-system';

/**
 * Internal dependencies
 */
import { useAPI } from '../../../../app/api';
import { useStory } from '../../../../app/story';
import { focusStyle } from '../../../panels/shared';
import isDefaultPage from '../../../../utils/isDefaultPage';
import { getStoryMarkup } from '../../../../output/index';
import { ReactComponent as Icon } from './images/illustration.svg';
import { useConfig } from '../../../../app/config';
import { useLocalMedia } from '../../../../app';
import LoadingIndicator from '../../../loading-indicator/LoadingIndicator';

const StyledText = styled(Text)`
  color: ${({ theme }) => theme.colors.fg.secondary};
`;

const IconWrapper = styled.div`
  width: 32px;
  height: 42px;
  margin-right: 29px;
  ${({ theme }) =>
    `svg {
    color: ${theme.colors.fg.primary};
    path:nth-child(1): {
      fill: ${theme.colors.fg.primary};
    };
    path:nth-child(2) {
      fill: ${theme.colors.fg.tertiary};
    }
    path:nth-child(3) {
      fill: ${theme.colors.fg.tertiary};
    }
  }`}
`;

const SaveButton = styled.button`
  border: 0;
  background: none;
  height: 56px;
  width: 100%;
  padding: 7px;
  background-color: ${({ theme }) =>
    theme.colors.interactiveBg.secondaryNormal};
  transition: background-color ${BUTTON_TRANSITION_TIMING};
  border-radius: ${({ theme }) => theme.borders.radius.small};
  display: flex;
  flex-direction: row;
  align-items: center;
  cursor: pointer;

  &:hover {
    background-color: ${({ theme }) =>
      theme.colors.interactiveBg.secondaryHover};
  }

  ${({ isDisabled, theme }) =>
    isDisabled &&
    `
      background-color: ${theme.colors.interactiveBg.disable};
      &:hover {
        background-color: ${theme.colors.interactiveBg.disable};
      }

      ${IconWrapper} svg {
        path:nth-child(2) {
          fill: ${theme.colors.fg.secondary};
        }
        path:nth-child(3) {
          fill: ${theme.colors.fg.secondary};
        }
      }

      ${StyledText} {
        color: ${theme.colors.fg.disable};
      }
  `}

  ${focusStyle};
`;

const LoadingIndicatorContainer = styled.div`
  position: absolute;
  margin-left: auto;
  margin-right: auto;
  left: 0;
  right: 0;
  text-align: center;
  svg {
    width: 24px;
    height: 24px;
  }
`;

function TemplateSave({ setShowDefaultTemplates, updateList }) {
  const {
    actions: { addPageTemplate },
  } = useAPI();

  const { isUploading } = useLocalMedia((state) => ({
    isUploading: state.state.isUploading,
  }));

  const { showSnackbar } = useSnackbar();
  const [isSaving, setIsSaving] = useState(false);

  const { currentPage, story } = useStory(
    ({ state: { currentPage, story } }) => ({
      currentPage,
      story,
    })
  );
  const { metadata } = useConfig();

  const isDisabled = useMemo(
    () =>
      (currentPage && isDefaultPage(currentPage)) || isSaving || isUploading,
    [currentPage, isSaving, isUploading]
  );

  const handleSaveTemplate = useCallback(
    async (e) => {
      e.preventDefault();
      if (isDisabled) {
        return;
      }
      setIsSaving(true);

      return Promise.resolve()
        .then(async () => {
          const storyMarkup = await getStoryMarkup(
            story,
            [currentPage],
            metadata
          );
          return addPageTemplate({
            ...currentPage,
            id: uuidv4(),
            title: null,
            posterUrl: null,
            storyMarkup,
          });
        })
        .then((addedTemplate) => {
          updateList?.(addedTemplate);
          showSnackbar({
            message: __('Page Template saved.', 'web-stories'),
            dismissable: true,
          });
        })
        .catch(() => {
          showSnackbar({
            message: __(
              'Unable to save the template. Please try again.',
              'web-stories'
            ),
            dismissable: true,
          });
        })
        .finally(() => {
          setIsSaving(false);
        });
      setShowDefaultTemplates(false);
    },
    [
      isDisabled,
      addPageTemplate,
      currentPage,
      setShowDefaultTemplates,
      showSnackbar,
      updateList,
      story,
      metadata,
    ]
  );

  const textId = useMemo(() => `template_save_btn_${uuidv4()}`, []);
  return (
    <SaveButton
      aria-disabled={isDisabled}
      onClick={handleSaveTemplate}
      aria-labelledby={textId}
      isDisabled={isDisabled}
      disabled={isDisabled}
    >
      <IconWrapper>
        <Icon />
      </IconWrapper>
      <StyledText
        id={textId}
        size={THEME_CONSTANTS.TYPOGRAPHY.PRESET_SIZES.SMALL}
      >
        {__('Save current page as template', 'web-stories')}
      </StyledText>
      {isSaving && (
        <LoadingIndicatorContainer>
          <LoadingIndicator />
        </LoadingIndicatorContainer>
      )}
    </SaveButton>
  );
}

TemplateSave.propTypes = {
  setShowDefaultTemplates: PropTypes.func.isRequired,
  updateList: PropTypes.func,
};

export default TemplateSave;
